import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  attendanceApi,
  attendancePunchHookApi,
  attendanceWeeklyTimesheetApi,
  employeesApi,
  leaveApi,
  wfhApi,
  regularizationApi,
  penaltyApi,
} from "proxyUrl";

const minutesToHHMM = (mins) => {
  if (mins === null || mins === undefined || Number.isNaN(mins)) return "-";
  const h = Math.floor(mins / 60)
    .toString()
    .padStart(2, "0");
  const m = Math.floor(mins % 60)
    .toString()
    .padStart(2, "0");
  return `${h}h ${m}m`;
};

const combineDateTime = (date, time) => {
  if (!date || !time) return null;
  return `${date}T${time}`;
};

const splitTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    // fallback if already time-only
    return value.slice(11, 16) || "";
  }
  return d.toISOString().slice(11, 16);
};

const getLast7DaysStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Helper function to format date string (YYYY-MM-DD) to display format (DD-MM-YYYY) in local timezone
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  // Parse YYYY-MM-DD in local timezone
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
};

// Helper function to parse date string (YYYY-MM-DD) to Date object in local timezone
const parseDateString = (dateString) => {
  if (!dateString) return null;
  // Parse YYYY-MM-DD in local timezone
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Helper function to format Date object to YYYY-MM-DD string in local timezone
const formatDateToISO = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const attendanceSchema = yup.object({
  id: yup.number().nullable(),
  employeeId: yup.string().required("Employee is required"),
  date: yup.string().required("Date is required"),
  clockInTime: yup.string().nullable(),
  clockOutTime: yup.string().nullable(),
  status: yup.string().required("Status is required"),
  location: yup.string().nullable(),
  note: yup.string().nullable(),
});

const leaveSchema = yup.object({
  id: yup.number().nullable(),
  employeeId: yup.string().required("Employee is required"),
  leaveType: yup.string().required("Leave type is required"),
  startDate: yup.string().required("Start date is required"),
  endDate: yup.string().required("End date is required"),
  totalDays: yup
    .number()
    .typeError("Total days must be a number")
    .required("Total days is required"),
  reason: yup.string().nullable(),
  status: yup.string().required("Status is required"),
});

const wfhSchema = yup.object({
  id: yup.number().nullable(),
  employeeId: yup.string().required("Employee is required"),
  startDate: yup.string().required("Start date is required"),
  endDate: yup.string().required("End date is required"),
  reason: yup.string().nullable(),
  status: yup.string().required("Status is required"),
});

const regularizationSchema = yup.object({
  id: yup.number().nullable(),
  attendanceId: yup.string().required("Attendance record is required"),
  employeeId: yup.string().required("Employee is required"),
  requestedClockIn: yup.string().nullable(),
  requestedClockOut: yup.string().nullable(),
  reason: yup.string().nullable(),
  status: yup.string().required("Status is required"),
});

const penaltySchema = yup.object({
  id: yup.number().nullable(),
  employeeId: yup.string().required("Employee is required"),
  date: yup.string().required("Date is required"),
  reason: yup.string().nullable(),
  amount: yup.string().nullable(),
  referenceType: yup.string().nullable(),
  referenceId: yup.string().nullable(),
});

const Attendance = () => {
  const [activeTab, setActiveTab] = useState("attendance");
  const [employees, setEmployees] = useState([]);

  // Attendance
  const [attendance, setAttendance] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceFilters, setAttendanceFilters] = useState({
    employeeId: "",
    status: "",
    startDate: "",
    endDate: "",
    date: "",
  });
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);

  const {
    register: attendanceRegister,
    handleSubmit: handleAttendanceSubmit,
    reset: resetAttendanceForm,
    getValues: getAttendanceValues,
    watch: watchAttendance,
    setValue: setAttendanceValue,
    formState: { errors: attendanceErrors },
  } = useForm({
    resolver: yupResolver(attendanceSchema),
    defaultValues: {
      id: null,
      employeeId: "",
      date: new Date().toISOString().slice(0, 10),
      clockInTime: "",
      clockOutTime: "",
      status: "present",
      location: "",
      note: "",
    },
  });

  // Leave
  const [leaveList, setLeaveList] = useState([]);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const {
    register: leaveRegister,
    handleSubmit: handleLeaveSubmit,
    reset: resetLeaveForm,
    watch: watchLeave,
    setValue: setLeaveValue,
    formState: { errors: leaveErrors },
  } = useForm({
    resolver: yupResolver(leaveSchema),
    defaultValues: {
      id: null,
      employeeId: "",
      leaveType: "",
      startDate: "",
      endDate: "",
      totalDays: "",
      reason: "",
      status: "pending",
    },
  });
  
  const leaveStartDate = watchLeave("startDate");
  const leaveEndDate = watchLeave("endDate");
  const leaveTotalDays = watchLeave("totalDays");
  
  // Date picker states
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [showAttendanceDatePicker, setShowAttendanceDatePicker] = useState(false);
  const [showFilterDatePicker, setShowFilterDatePicker] = useState(false);
  const [showFilterStartDatePicker, setShowFilterStartDatePicker] = useState(false);
  const [showFilterEndDatePicker, setShowFilterEndDatePicker] = useState(false);
  const [showWfhStartDatePicker, setShowWfhStartDatePicker] = useState(false);
  const [showWfhEndDatePicker, setShowWfhEndDatePicker] = useState(false);
  const [showPenaltyDatePicker, setShowPenaltyDatePicker] = useState(false);
  const [showTimesheetDatePicker, setShowTimesheetDatePicker] = useState(false);
  const [showTimesheetWeekStartPicker, setShowTimesheetWeekStartPicker] = useState(false);
  const [showAllFilterWeekStartPicker, setShowAllFilterWeekStartPicker] = useState(false);
  
  // Auto-calculate totalDays when dates change
  const [isDaysManuallyEdited, setIsDaysManuallyEdited] = useState(false);
  
  useEffect(() => {
    // Always auto-calculate when dates change, reset manual edit flag
    if (leaveStartDate && leaveEndDate) {
      // Parse dates in local timezone to avoid timezone issues
      const start = parseDateString(leaveStartDate);
      const end = parseDateString(leaveEndDate);
      if (start && end) {
        // Calculate difference in days using local dates
        const diffTime = Math.abs(end - start);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        setLeaveValue("totalDays", diffDays.toString(), { shouldValidate: true, shouldDirty: true });
        setIsDaysManuallyEdited(false); // Reset manual edit flag when dates change
      }
    } else if (!leaveStartDate || !leaveEndDate) {
      setLeaveValue("totalDays", "", { shouldValidate: true, shouldDirty: true });
      setIsDaysManuallyEdited(false);
    }
  }, [leaveStartDate, leaveEndDate, setLeaveValue]);

  // Close date pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.react-calendar') && !target.closest('input[readonly]')) {
        if (showStartDatePicker) setShowStartDatePicker(false);
        if (showEndDatePicker) setShowEndDatePicker(false);
        if (showAttendanceDatePicker) setShowAttendanceDatePicker(false);
        if (showFilterDatePicker) setShowFilterDatePicker(false);
        if (showFilterStartDatePicker) setShowFilterStartDatePicker(false);
        if (showFilterEndDatePicker) setShowFilterEndDatePicker(false);
        if (showWfhStartDatePicker) setShowWfhStartDatePicker(false);
        if (showWfhEndDatePicker) setShowWfhEndDatePicker(false);
        if (showPenaltyDatePicker) setShowPenaltyDatePicker(false);
        if (showTimesheetDatePicker) setShowTimesheetDatePicker(false);
        if (showTimesheetWeekStartPicker) setShowTimesheetWeekStartPicker(false);
        if (showAllFilterWeekStartPicker) setShowAllFilterWeekStartPicker(false);
      }
    };

    const anyPickerOpen = showStartDatePicker || showEndDatePicker || showAttendanceDatePicker || 
                         showFilterDatePicker || showFilterStartDatePicker || showFilterEndDatePicker ||
                         showWfhStartDatePicker || showWfhEndDatePicker || showPenaltyDatePicker ||
                         showTimesheetDatePicker || showTimesheetWeekStartPicker || showAllFilterWeekStartPicker;
    
    if (anyPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showStartDatePicker, showEndDatePicker, showAttendanceDatePicker, showFilterDatePicker, 
      showFilterStartDatePicker, showFilterEndDatePicker, showWfhStartDatePicker, 
      showWfhEndDatePicker, showPenaltyDatePicker, showTimesheetDatePicker, 
      showTimesheetWeekStartPicker, showAllFilterWeekStartPicker]);

  // WFH
  const [wfhList, setWfhList] = useState([]);
  const [wfhModalOpen, setWfhModalOpen] = useState(false);
  const {
    register: wfhRegister,
    handleSubmit: handleWfhSubmit,
    reset: resetWfhForm,
    watch: watchWfh,
    setValue: setWfhValue,
    formState: { errors: wfhErrors },
  } = useForm({
    resolver: yupResolver(wfhSchema),
    defaultValues: {
      id: null,
      employeeId: "",
      startDate: "",
      endDate: "",
      reason: "",
      status: "pending",
    },
  });

  // Regularization
  const [regularizationList, setRegularizationList] = useState([]);
  const [regularizationModalOpen, setRegularizationModalOpen] = useState(false);
  const {
    register: regularizationRegister,
    handleSubmit: handleRegularizationSubmit,
    reset: resetRegularizationForm,
    formState: { errors: regularizationErrors },
  } = useForm({
    resolver: yupResolver(regularizationSchema),
    defaultValues: {
      id: null,
      attendanceId: "",
      employeeId: "",
      requestedClockIn: "",
      requestedClockOut: "",
      reason: "",
      status: "pending",
    },
  });

  // Penalties
  const [penaltyList, setPenaltyList] = useState([]);
  const [penaltyModalOpen, setPenaltyModalOpen] = useState(false);
  const {
    register: penaltyRegister,
    handleSubmit: handlePenaltySubmit,
    reset: resetPenaltyForm,
    watch: watchPenalty,
    setValue: setPenaltyValue,
    formState: { errors: penaltyErrors },
  } = useForm({
    resolver: yupResolver(penaltySchema),
    defaultValues: {
      id: null,
      employeeId: "",
      date: "",
      reason: "",
      amount: "",
      referenceType: "",
      referenceId: "",
    },
  });

  // Timesheet view
  const [timesheetDate, setTimesheetDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [timesheetWeekStart, setTimesheetWeekStart] = useState(
    getLast7DaysStart()
  );
  const [timesheetView, setTimesheetView] = useState("day"); // "day" or "week"
  const [weeklyTimesheet, setWeeklyTimesheet] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);

  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      const id = e.id || e._id || e.userId || e.user_id;
      map[id] = e;
    });
    return map;
  }, [employees]);

  const getEmployeeName = (id) => {
    const emp = employeeMap[id];
    if (!emp) return id || "-";
    return emp.fullName || emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || id;
  };

  // Fetch helpers
  const fetchEmployees = async () => {
    try {
      const { data } = await axios.get(employeesApi, { withCredentials: true });
      setEmployees(data?.data || data?.users || data || []);
    } catch (error) {
      console.error("employees fetch error", error);
      toast.error("Unable to load employees");
    }
  };

  const fetchAttendance = async (overrideFilters) => {
    setAttendanceLoading(true);
    try {
      const paramsSource = overrideFilters || attendanceFilters;
      const params = {};
      Object.entries(paramsSource).forEach(([key, val]) => {
        if (val) params[key] = val;
      });
      const { data } = await axios.get(attendanceApi, {
        params,
        withCredentials: true,
      });
      setAttendance(data?.data || []);
    } catch (error) {
      console.error("attendance fetch error", error);
      toast.error("Unable to load attendance");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchLeave = async () => {
    try {
      const { data } = await axios.get(leaveApi, { withCredentials: true });
      setLeaveList(data?.data || []);
    } catch (error) {
      console.error("leave fetch error", error);
    }
  };

  const fetchWfh = async () => {
    try {
      const { data } = await axios.get(wfhApi, { withCredentials: true });
      setWfhList(data?.data || []);
    } catch (error) {
      console.error("wfh fetch error", error);
    }
  };

  const fetchRegularization = async () => {
    try {
      const { data } = await axios.get(regularizationApi, {
        withCredentials: true,
      });
      setRegularizationList(data?.data || []);
    } catch (error) {
      console.error("regularization fetch error", error);
    }
  };

  const fetchPenalties = async () => {
    try {
      const { data } = await axios.get(penaltyApi, { withCredentials: true });
      setPenaltyList(data?.data || []);
    } catch (error) {
      console.error("penalty fetch error", error);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchAttendance();
    fetchLeave();
    fetchWfh();
    fetchRegularization();
    fetchPenalties();
    // Load weekly view for current week by default
    fetchWeeklyTimesheet();
  }, []);

  // Attendance handlers
  const openAttendanceModal = (record) => {
    if (record) {
      resetAttendanceForm({
        id: record.id,
        employeeId: record.employeeId,
        date: record.date,
        clockInTime: splitTime(record.clockIn),
        clockOutTime: splitTime(record.clockOut),
        status: record.status || "present",
        location: record.location || "",
        note: record.note || "",
      });
    } else {
      resetAttendanceForm({
        id: null,
        employeeId: "",
        date: new Date().toISOString().slice(0, 10),
        clockInTime: "",
        clockOutTime: "",
        status: "present",
        location: "",
        note: "",
      });
    }
    setAttendanceModalOpen(true);
  };

  const onSubmitAttendance = async (values) => {
    const payload = {
      employeeId: values.employeeId,
      date: values.date,
      clockIn: combineDateTime(values.date, values.clockInTime),
      clockOut: combineDateTime(values.date, values.clockOutTime),
      status: values.status,
      location: values.location || null,
      note: values.note || null,
      source: "manual",
    };
    try {
      if (values.id) {
        await axios.put(`${attendanceApi}/${values.id}`, payload, {
          withCredentials: true,
        });
        toast.success("Attendance updated");
      } else {
        await axios.post(attendanceApi, payload, { withCredentials: true });
        toast.success("Attendance saved");
      }
      setAttendanceModalOpen(false);
      fetchAttendance();
    } catch (error) {
      console.error("attendance save error", error);
      toast.error("Unable to save attendance");
    }
  };

  // Leave handlers
  const openLeaveModal = (record) => {
    setIsDaysManuallyEdited(false);
    setShowStartDatePicker(false);
    setShowEndDatePicker(false);
    if (record) {
      resetLeaveForm({
        id: record.id,
        employeeId: record.employeeId,
        leaveType: record.leaveType,
        startDate: record.startDate,
        endDate: record.endDate,
        totalDays: record.totalDays,
        reason: record.reason || "",
        status: record.status || "pending",
      });
    } else {
      resetLeaveForm({
        id: null,
        employeeId: "",
        leaveType: "Sick Leave",
        startDate: "",
        endDate: "",
        totalDays: "",
        reason: "",
        status: "pending",
      });
    }
    setLeaveModalOpen(true);
  };

  const onSubmitLeave = async (values) => {
    // Calculate totalDays if not provided
    let totalDays = Number(values.totalDays);
    if (!totalDays && values.startDate && values.endDate) {
      const start = new Date(values.startDate);
      const end = new Date(values.endDate);
      const diffTime = Math.abs(end - start);
      totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }
    
    const payload = {
      employeeId: values.employeeId,
      leaveType: values.leaveType,
      startDate: values.startDate,
      endDate: values.endDate,
      totalDays: totalDays,
      reason: values.reason || null,
      status: values.status,
    };
    try {
      await axios.post(leaveApi, payload, { withCredentials: true });
      toast.success("Leave created");
      setLeaveModalOpen(false);
      fetchLeave();
    } catch (error) {
      console.error("leave save error", error);
      toast.error("Unable to save leave");
    }
  };

  const updateLeaveStatus = async (record, status) => {
    try {
      await axios.put(
        `${leaveApi}/${record.id}/status`,
        { status },
        { withCredentials: true }
      );
      toast.success("Leave updated");
      fetchLeave();
    } catch (error) {
      console.error("leave status error", error);
      toast.error("Unable to update leave");
    }
  };

  // WFH handlers
  const openWfhModal = (record) => {
    if (record) {
      resetWfhForm({
        id: record.id,
        employeeId: record.employeeId,
        startDate: record.startDate,
        endDate: record.endDate,
        reason: record.reason || "",
        status: record.status || "pending",
      });
    } else {
      resetWfhForm({
        id: null,
        employeeId: "",
        startDate: "",
        endDate: "",
        reason: "",
        status: "pending",
      });
    }
    setWfhModalOpen(true);
  };

  const onSubmitWfh = async (values) => {
    const payload = {
      employeeId: values.employeeId,
      startDate: values.startDate,
      endDate: values.endDate,
      reason: values.reason || null,
      status: values.status,
    };
    try {
      await axios.post(wfhApi, payload, { withCredentials: true });
      toast.success("WFH request created");
      setWfhModalOpen(false);
      fetchWfh();
    } catch (error) {
      console.error("wfh save error", error);
      toast.error("Unable to save WFH");
    }
  };

  const updateWfhStatus = async (record, status) => {
    try {
      await axios.put(
        `${wfhApi}/${record.id}/status`,
        { status },
        { withCredentials: true }
      );
      toast.success("WFH updated");
      fetchWfh();
    } catch (error) {
      console.error("wfh status error", error);
      toast.error("Unable to update WFH");
    }
  };

  // Regularization
  const openRegularizationModal = (record) => {
    if (record) {
      resetRegularizationForm({
        id: record.id,
        attendanceId: record.attendanceId,
        employeeId: record.employeeId,
        requestedClockIn: splitTime(record.requestedClockIn),
        requestedClockOut: splitTime(record.requestedClockOut),
        reason: record.reason || "",
        status: record.status || "pending",
      });
    } else {
      resetRegularizationForm({
        id: null,
        attendanceId: "",
        employeeId: "",
        requestedClockIn: "",
        requestedClockOut: "",
        reason: "",
        status: "pending",
      });
    }
    setRegularizationModalOpen(true);
  };

  const onSubmitRegularization = async (values) => {
    const record = attendance.find(
      (a) => String(a.id) === String(values.attendanceId)
    );
    const attDate = record?.date || null;
    const payload = {
      attendanceId: values.attendanceId,
      employeeId: values.employeeId,
      requestedClockIn: combineDateTime(
        attDate,
        values.requestedClockIn
      ),
      requestedClockOut: combineDateTime(
        attDate,
        values.requestedClockOut
      ),
      reason: values.reason || null,
      status: values.status,
    };
    try {
      await axios.post(regularizationApi, payload, { withCredentials: true });
      toast.success("Regularization created");
      setRegularizationModalOpen(false);
      fetchRegularization();
      fetchAttendance();
    } catch (error) {
      console.error("regularization save error", error);
      toast.error("Unable to save regularization");
    }
  };

  const updateRegularizationStatus = async (record, status) => {
    try {
      await axios.put(
        `${regularizationApi}/${record.id}/status`,
        { status },
        { withCredentials: true }
      );
      toast.success("Regularization updated");
      fetchRegularization();
      fetchAttendance();
    } catch (error) {
      console.error("regularization status error", error);
      toast.error("Unable to update regularization");
    }
  };

  // Penalty
  const openPenaltyModal = (record) => {
    if (record) {
      resetPenaltyForm({
        id: record.id,
        employeeId: record.employeeId,
        date: record.date,
        reason: record.reason || "",
        amount: record.amount || "",
        referenceType: record.referenceType || "",
        referenceId: record.referenceId || "",
      });
    } else {
      resetPenaltyForm({
        id: null,
        employeeId: "",
        date: "",
        reason: "",
        amount: "",
        referenceType: "",
        referenceId: "",
      });
    }
    setPenaltyModalOpen(true);
  };

  const onSubmitPenalty = async (values) => {
    const payload = {
      employeeId: values.employeeId,
      date: values.date,
      reason: values.reason || null,
      amount: values.amount || null,
      referenceType: values.referenceType || null,
      referenceId: values.referenceId || null,
    };
    try {
      await axios.post(penaltyApi, payload, { withCredentials: true });
      toast.success("Penalty created");
      setPenaltyModalOpen(false);
      fetchPenalties();
    } catch (error) {
      console.error("penalty save error", error);
      toast.error("Unable to save penalty");
    }
  };

  const handleDeletePenalty = async (id) => {
    const confirmed = window.confirm("Delete this penalty?");
    if (!confirmed) return;
    try {
      await axios.delete(`${penaltyApi}/${id}`, { withCredentials: true });
      toast.success("Penalty removed");
      setPenaltyList((prev) => prev.filter((p) => p.id !== id));
    } catch (error) {
      console.error("penalty delete error", error);
      toast.error("Unable to delete penalty");
    }
  };

  // Punch hook (manual trigger)
  const triggerPunchHook = async () => {
    const values = getAttendanceValues();
    if (!values.employeeId || !values.date || !values.clockInTime) {
      toast.error("Employee, date and punch time are required");
      return;
    }
    try {
      await axios.post(
        attendancePunchHookApi,
        {
          employeeId: values.employeeId,
          punchTime: combineDateTime(values.date, values.clockInTime),
          direction: "auto",
          source: "manual",
        },
        { withCredentials: true }
      );
      toast.success("Punch recorded");
      fetchAttendance();
    } catch (error) {
      console.error("punch hook error", error);
      toast.error("Unable to record punch");
    }
  };

  // Derived stats
  const attendanceStats = useMemo(() => {
    const present = attendance.filter((r) => r.status === "present").length;
    const absent = attendance.filter((r) => r.status === "absent").length;
    const late = attendance.filter((r) => r.status === "late").length;
    return {
      total: attendance.length,
      present,
      absent,
      late,
    };
  }, [attendance]);

  const dayAttendance = useMemo(() => {
    return attendance.filter((r) => r.date === timesheetDate);
  }, [attendance, timesheetDate]);

  const weekAttendance = useMemo(() => {
    if (!timesheetWeekStart) return [];
    const start = new Date(timesheetWeekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return attendance.filter((r) => {
      const d = new Date(r.date);
      return d >= start && d <= end;
    });
  }, [attendance, timesheetWeekStart]);

  const fetchWeeklyTimesheet = async () => {
    if (!timesheetWeekStart) return;
    setWeeklyLoading(true);
    try {
      const params = {
        startDate: timesheetWeekStart,
      };
      if (attendanceFilters.employeeId) params.employeeId = attendanceFilters.employeeId;
      if (attendanceFilters.status) params.status = attendanceFilters.status;
      const { data } = await axios.get(attendanceWeeklyTimesheetApi, {
        params,
        withCredentials: true,
      });
      setWeeklyTimesheet(data || null);
    } catch (error) {
      console.error("weekly timesheet fetch error", error);
      toast.error("Unable to load weekly timesheet");
    } finally {
      setWeeklyLoading(false);
    }
  };

  const renderStatusBadge = (status) => {
    const colors = {
      present: "bg-green-100 text-green-700",
      absent: "bg-red-100 text-red-700",
      late: "bg-yellow-100 text-yellow-700",
      pending: "bg-yellow-100 text-yellow-700",
      approved: "bg-green-100 text-green-700",
      disapproved: "bg-red-100 text-red-700",
      rejected: "bg-red-100 text-red-700",
    };
    return (
      <span
        className={`rounded-full px-3 py-1 text-xs font-normal ${
          colors[status] || "bg-gray-100 text-gray-700"
        }`}
      >
        {status || "-"}
      </span>
    );
  };

  const SectionCard = ({ title, children, actions }) => (
    <div className="rounded-xl bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <div className="flex items-center gap-2">{actions}</div>
      </div>
      {children}
    </div>
  );

  const SimpleModal = ({ open, title, onClose, children, footer, slideFromRight = false }) => {
    if (!open) return null;
    
    if (slideFromRight) {
    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity duration-300 animate-in fade-in"
            onClick={onClose}
          />
          {/* Side Panel */}
          <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
            <div className="relative flex w-screen max-w-md flex-col overflow-y-auto bg-white shadow-xl transform transition-transform duration-300 ease-out translate-x-0">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-5">
                <h4 className="text-xl font-bold text-gray-900">{title}</h4>
            <button
              onClick={onClose}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
            </button>
          </div>
              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
              {/* Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4">
                {footer}
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm px-4 animate-in fade-in duration-200">
        <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl animate-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-6 py-4">
            <h4 className="text-xl font-bold text-gray-900">{title}</h4>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto px-6 py-5 bg-gray-50/30">{children}</div>
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4">
            {footer}
          </div>
        </div>
      </div>
    );
  };

  const AttendanceTable = () => (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Clock In
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Clock Out
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {attendanceLoading && (
              <tr>
                <td colSpan={8} className="px-6 py-6 text-center text-sm text-gray-500">
                  <div className="flex items-center justify-center gap-2">
                    <svg className="h-5 w-5 animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  Loading...
                  </div>
                </td>
              </tr>
            )}
            {!attendanceLoading && attendance.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-500">No records found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                  </div>
                </td>
              </tr>
            )}
            {attendance.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-6 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-sm font-semibold text-white">
                      {getEmployeeName(item.employeeId).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{getEmployeeName(item.employeeId)}</div>
                      <div className="text-xs text-gray-500">ID: {item.employeeId}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{item.date}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.clockIn ? new Date(item.clockIn).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.clockOut ? new Date(item.clockOut).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                  {minutesToHHMM(item.totalMinutes)}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                  {item.location || <span className="text-gray-400">-</span>}
                </td>
                <td className="px-6 py-3 whitespace-nowrap">{renderStatusBadge(item.status)}</td>
                <td className="px-6 py-3 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => openAttendanceModal(item)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100"
                    >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                      Edit
                    </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const DayTimesheet = () => (
    <div className="space-y-3">
      {dayAttendance.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 p-12">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 mb-4">
            <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-gray-600">No entries for this date</p>
          <p className="mt-1 text-xs text-gray-400">Select a different date to view attendance records</p>
        </div>
      )}
      {dayAttendance.map((item) => {
        const start = item.clockIn ? new Date(item.clockIn) : null;
        const end = item.clockOut ? new Date(item.clockOut) : null;
        const startMinutes = start ? start.getHours() * 60 + start.getMinutes() : 0;
        const endMinutes = end ? end.getHours() * 60 + end.getMinutes() : startMinutes + 30;
        const width = Math.max(((endMinutes - startMinutes) / (24 * 60)) * 100, 2);
        const left = (startMinutes / (24 * 60)) * 100;
        return (
          <div
            key={item.id}
            className="group rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:shadow-md hover:border-indigo-200"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white font-semibold text-sm">
                  {getEmployeeName(item.employeeId).charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">
                {getEmployeeName(item.employeeId)}
              </div>
                  <div className="text-xs text-gray-500">
                    {item.status || 'Present'}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-gray-900">
                {minutesToHHMM(item.totalMinutes)}
              </div>
                <div className="text-xs text-gray-500">
                  {start ? start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--"} - {end ? end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--"}
            </div>
              </div>
            </div>
            <div className="relative h-4 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className="absolute h-4 rounded-full bg-gradient-to-r from-indigo-500 to-indigo-600 transition-all duration-300"
                style={{ width: `${width}%`, left: `${left}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  const WeekTimesheet = () => {
    const days = [];
    if (timesheetWeekStart) {
      const start = new Date(timesheetWeekStart);
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        days.push(d.toISOString().slice(0, 10));
      }
    }
    const grouped = {};
    weekAttendance.forEach((item) => {
      if (!grouped[item.employeeId]) grouped[item.employeeId] = {};
      grouped[item.employeeId][item.date] = item;
    });
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
            <tr>
              <th className="sticky left-0 z-10 bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Name
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Total Time (H)
              </th>
              {days.map((d) => (
                <th
                  key={d}
                  className="px-4 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-700 min-w-[100px]"
                >
                  <div className="flex flex-col items-center">
                    <span className="text-gray-900">
                  {new Date(d).toLocaleDateString(undefined, {
                    day: "2-digit",
                    month: "short",
                  })}
                    </span>
                    <span className="mt-1 text-[10px] font-normal text-gray-500">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(d).getDay()]}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {Object.keys(grouped).map((empId) => {
              const totalMinutes = Object.values(grouped[empId]).reduce(
                (acc, rec) => acc + (rec.totalMinutes || 0),
                0
              );
              return (
                <tr key={empId} className="hover:bg-gray-50/50 transition-colors group">
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50/50 px-6 py-4 whitespace-nowrap transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-purple-600 text-white font-semibold text-xs">
                        {getEmployeeName(empId).charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-semibold text-gray-900">
                    {getEmployeeName(empId)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-bold text-gray-900">
                    {minutesToHHMM(totalMinutes)}
                    </span>
                  </td>
                  {days.map((d) => {
                    const rec = grouped[empId][d];
                    const start = rec?.clockIn ? new Date(rec.clockIn) : null;
                    const end = rec?.clockOut ? new Date(rec.clockOut) : null;
                    const startMinutes = start
                      ? start.getHours() * 60 + start.getMinutes()
                      : 0;
                    const endMinutes = end
                      ? end.getHours() * 60 + end.getMinutes()
                      : startMinutes + 30;
                    const width = rec
                      ? Math.max(((endMinutes - startMinutes) / (24 * 60)) * 100, 5)
                      : 0;
                    const left = rec ? (startMinutes / (24 * 60)) * 100 : 0;
                    return (
                      <td key={d} className="px-4 py-4">
                        {rec ? (
                          <div className="space-y-2">
                            <div className="relative h-3 w-full rounded-full bg-gray-100 overflow-hidden">
                              <div
                                className="absolute h-3 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-300"
                                style={{ width: `${width}%`, left: `${left}%` }}
                              />
                            </div>
                            <div className="text-center">
                              <span className="text-xs font-semibold text-gray-700">
                              {minutesToHHMM(rec.totalMinutes)}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <span className="text-xs text-gray-300">-</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {Object.keys(grouped).length === 0 && (
              <tr>
                <td
                  colSpan={days.length + 2}
                  className="px-6 py-12 text-center"
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 mb-4">
                      <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-gray-600">No records for this week</p>
                    <p className="mt-1 text-xs text-gray-400">Select a different week to view attendance records</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const AllWeeklyTimeline = () => {
    if (!weeklyTimesheet) {
      return (
        <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
          Select a week and click Apply to load the report.
        </div>
      );
    }
    const { employees: empRows = [], startDate } = weeklyTimesheet;
    const days = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push({
        iso: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }),
      });
    }

    const statusColor = (status) => {
      switch (status) {
        case "present":
          return "bg-green-50 text-green-700 border-green-200";
        case "late":
          return "bg-yellow-50 text-yellow-700 border-yellow-200";
        case "leave":
          return "bg-purple-50 text-purple-700 border-purple-200";
        case "wfh":
          return "bg-indigo-50 text-indigo-700 border-indigo-200";
        case "regularized":
          return "bg-orange-50 text-orange-700 border-orange-200";
        case "absent":
        default:
          return "bg-red-50 text-red-700 border-red-200";
      }
    };

    return (
      <div className="overflow-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
            <tr>
              <th className="sticky left-0 z-10 bg-gradient-to-r from-gray-50 to-gray-100/50 px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                Department
              </th>
              {days.map((d) => (
                <th
                  key={d.iso}
                  className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-700 min-w-[120px]"
                >
                  <div className="flex flex-col items-center">
                    <span className="text-gray-900 font-semibold">
                      {new Date(d.iso).toLocaleDateString(undefined, {
                        day: "2-digit",
                        month: "short",
                      })}
                    </span>
                    <span className="mt-1 text-[10px] font-normal text-gray-500">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(d.iso).getDay()]}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {empRows.length === 0 && !weeklyLoading && (
              <tr>
                <td
                  colSpan={days.length + 2}
                  className="px-6 py-8 text-center"
                >
                  <div className="flex flex-col items-center justify-center">
                    <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-500">No data for this week</p>
                  </div>
                </td>
              </tr>
            )}
            {weeklyLoading && (
              <tr>
                <td
                  colSpan={days.length + 2}
                  className="px-6 py-8 text-center"
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="h-5 w-5 animate-spin text-indigo-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm text-gray-500">Loading weekly timesheet...</span>
                  </div>
                </td>
              </tr>
            )}
            {empRows.map((emp) => (
              <tr key={emp.employeeId} className="bg-white hover:bg-gray-50/50 transition-colors">
                <td className="sticky left-0 z-10 bg-white hover:bg-gray-50/50 px-6 py-3 whitespace-nowrap transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-sm font-semibold text-white">
                      {emp.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{emp.name}</div>
                      <div className="text-xs text-gray-500">
                        {emp.designation || ""} {emp.employeeCode ? `• ${emp.employeeCode}` : ""}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-700">
                  {emp.department || <span className="text-gray-400">-</span>}
                </td>
                {days.map((d, idx) => {
                  const day = emp.days?.[idx];
                  if (!day) {
                    return (
                      <td key={d.iso} className="px-4 py-3 text-center">
                        <span className="text-sm text-gray-300">-</span>
                      </td>
                    );
                  }
                  const penaltiesText =
                    day.penalties && day.penalties.length
                      ? day.penalties
                          .map((p) => {
                            const amountPart = p.amount ? `${p.amount}` : "N/A";
                            const reasonPart = p.reason ? `(${p.reason})` : "";
                            return `Penalty: ${amountPart} ${reasonPart}`.trim();
                          })
                          .join(" | ")
                      : "";
                  return (
                    <td key={d.iso} className="px-4 py-3 text-center">
                      <div className="group relative inline-flex items-center justify-center">
                        <div
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-normal ${statusColor(
                            day.status
                          )}`}
                        >
                          <span>{day.label}</span>
                          {day.penalties && day.penalties.length > 0 && (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                              {day.penalties.length}
                            </span>
                          )}
                        </div>
                        {day.penalties && day.penalties.length > 0 && penaltiesText && (
                          <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-xl group-hover:block">
                            {penaltiesText}
                            <div className="absolute left-1/2 top-full -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6 px-4">
      <div className="relative mt-6 overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-600 p-6 text-white shadow-xl">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.05\"%3E%3Cpath d=\"M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"}}></div>
        <div className="relative flex items-center justify-between">
        <div>
            <h1 className="text-xl font-bold mb-1">Attendance Management</h1>
            <p className="text-sm text-indigo-50/90">Track and manage employee attendance, leaves, and work from home</p>
        </div>
          <div className="flex gap-3">
          <button
            onClick={() => openAttendanceModal()}
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-indigo-600 shadow-md transition-all hover:-translate-y-[1px] hover:shadow-lg focus:outline-none"
          >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            Add Attendance
          </button>
          <button
            onClick={() => openLeaveModal()}
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 bg-white/10 backdrop-blur-sm px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-white/20 hover:border-white/60"
          >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            Add Leave
          </button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Attendance Card */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-indigo-100/50 p-4 shadow-sm border border-indigo-200/60 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-indigo-700/70 uppercase tracking-wide mb-1">Total Attendance</p>
              <p className="text-2xl font-bold text-indigo-900">{attendanceStats.total}</p>
        </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100">
              <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Present Card */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50/50 p-4 shadow-sm border border-green-200/60 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-green-700/70 uppercase tracking-wide mb-1">Present</p>
              <p className="text-2xl font-bold text-green-700">
            {attendanceStats.present}
          </p>
        </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Absentees Card */}
        <div className="rounded-lg bg-gradient-to-br from-red-50 to-rose-50/50 p-4 shadow-sm border border-red-200/60 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-red-700/70 uppercase tracking-wide mb-1">Absentees</p>
              <p className="text-2xl font-bold text-red-700">
            {attendanceStats.absent}
          </p>
        </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
              <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Late Login Card */}
        <div className="rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50/50 p-4 shadow-sm border border-amber-200/60 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-amber-700/70 uppercase tracking-wide mb-1">Late Login</p>
              <p className="text-2xl font-bold text-amber-700">
            {attendanceStats.late}
          </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
              <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-white p-5 shadow-sm border border-gray-100">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-4">
          <div className="flex flex-wrap items-center gap-2">
          {["attendance", "timesheet", "all", "leave", "wfh", "regularization", "penalty"].map(
            (tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                  className={`rounded-lg px-4 py-2.5 text-sm font-semibold capitalize transition-all ${
                  activeTab === tab
                      ? "bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-md"
                      : "text-[#444] hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                  {tab === "wfh" ? "WFH" : tab}
              </button>
            )
          )}
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "leave" && (
              <button
                onClick={() => openLeaveModal()}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-[1px]"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Leave
              </button>
            )}
            {activeTab === "wfh" && (
              <button
                onClick={() => openWfhModal()}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-[1px]"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add WFH
              </button>
            )}
            {activeTab === "regularization" && (
              <button
                onClick={() => openRegularizationModal()}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-[1px]"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Regularization
              </button>
            )}
            {activeTab === "penalty" && (
              <button
                onClick={() => openPenaltyModal()}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-[1px]"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Penalty
              </button>
            )}
          </div>
        </div>

        {activeTab === "attendance" && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-end gap-4 flex-wrap">
                {/* Employee Filter */}
                <div className="flex flex-col gap-2 flex-1 min-w-[150px]">
                <label
                  htmlFor="attendanceFilterEmployee"
                    className="text-xs font-semibold text-[#444] uppercase tracking-wider"
                >
                  Employee
                </label>
                  <div className="relative">
                <select
                  id="attendanceFilterEmployee"
                  name="employeeId"
                  value={attendanceFilters.employeeId}
                  onChange={(e) =>
                    setAttendanceFilters((prev) => ({ ...prev, employeeId: e.target.value }))
                  }
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors appearance-none"
                      style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                >
                  <option value="">All employees</option>
                  {employees.map((emp) => {
                    const id = emp.id || emp._id || emp.userId || emp.user_id;
                    return (
                      <option key={id} value={id}>
                        {getEmployeeName(id)}
                      </option>
                    );
                  })}
                </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
              </div>
                  </div>
                </div>

                {/* Status Filter */}
                <div className="flex flex-col gap-2 min-w-[150px]">
                <label htmlFor="attendanceFilterStatus" className="text-xs font-semibold text-[#444] uppercase tracking-wider">
                  Status
                </label>
                  <div className="relative">
                <select
                  id="attendanceFilterStatus"
                  name="status"
                  value={attendanceFilters.status}
                  onChange={(e) =>
                    setAttendanceFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors appearance-none"
                      style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                >
                  <option value="">All status</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
              </div>
                  </div>
                </div>

                {/* Specific Date */}
                <div className="flex flex-col gap-2 min-w-[150px]">
                <label
                  htmlFor="attendanceFilterDate"
                    className="text-xs font-semibold text-[#444] uppercase tracking-wider"
                >
                    Specific Date
                </label>
                  <div className="relative">
                <input
                  id="attendanceFilterDate"
                  name="date"
                      type="text"
                      readOnly
                      value={formatDateForDisplay(attendanceFilters.date)}
                      onClick={() => {
                        setShowFilterDatePicker(!showFilterDatePicker);
                        setShowFilterStartDatePicker(false);
                        setShowFilterEndDatePicker(false);
                        setShowAttendanceDatePicker(false);
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors cursor-pointer"
                      placeholder=""
                    />
                    {!attendanceFilters.date && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                        <span className="text-xs text-gray-400">Select date</span>
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {showFilterDatePicker && (
                      <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                        <Calendar
                          onChange={(date) => {
                            const formattedDate = date ? formatDateToISO(date) : '';
                            setAttendanceFilters((prev) => ({ ...prev, date: formattedDate }));
                            setShowFilterDatePicker(false);
                          }}
                          value={parseDateString(attendanceFilters.date)}
                          formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                          prevLabel={<span className="text-purple-600">‹</span>}
                          nextLabel={<span className="text-purple-600">›</span>}
                          prev2Label={null}
                          next2Label={null}
                          tileClassName={({ date, view }) => {
                            if (view === 'month') {
                              const day = date.getDay();
                              if (day === 0 || day === 6) {
                                return 'weekend-day';
                              }
                            }
                            return null;
                          }}
                />
              </div>
                    )}
                  </div>
                </div>

                {/* Start Date */}
                <div className="flex flex-col gap-2 min-w-[150px]">
                <label
                  htmlFor="attendanceFilterStartDate"
                    className="text-xs font-semibold text-[#444] uppercase tracking-wider"
                >
                    Start Date
                </label>   
                  <div className="relative"> 
                <input
                  id="attendanceFilterStartDate"
                  name="startDate"
                      type="text"
                      readOnly
                      value={formatDateForDisplay(attendanceFilters.startDate)}
                      onClick={() => {
                        setShowFilterStartDatePicker(!showFilterStartDatePicker);
                        setShowFilterDatePicker(false);
                        setShowFilterEndDatePicker(false);
                        setShowAttendanceDatePicker(false);
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors cursor-pointer"
                      placeholder=""
                    />
                    {!attendanceFilters.startDate && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                        <span className="text-xs text-gray-400">Select date</span>
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {showFilterStartDatePicker && (
                      <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                        <Calendar
                          onChange={(date) => {
                            const formattedDate = date ? formatDateToISO(date) : '';
                            setAttendanceFilters((prev) => ({ ...prev, startDate: formattedDate }));
                            setShowFilterStartDatePicker(false);
                          }}
                          value={parseDateString(attendanceFilters.startDate)}
                          formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                          prevLabel={<span className="text-purple-600">‹</span>}
                          nextLabel={<span className="text-purple-600">›</span>}
                          prev2Label={null}
                          next2Label={null}
                          tileClassName={({ date, view }) => {
                            if (view === 'month') {
                              const day = date.getDay();
                              if (day === 0 || day === 6) {
                                return 'weekend-day';
                              }
                            }
                            return null;
                          }}
                />
              </div>
                    )}
                  </div>
                </div>

                {/* End Date */}
                <div className="flex flex-col gap-2 min-w-[150px]">
                <label
                  htmlFor="attendanceFilterEndDate"
                    className="text-xs font-semibold text-[#444] uppercase tracking-wider"
                >
                    End Date
                </label>
                  <div className="relative">
                <input
                  id="attendanceFilterEndDate"
                  name="endDate"
                      type="text"
                      readOnly
                      value={formatDateForDisplay(attendanceFilters.endDate)}
                      onClick={() => {
                        setShowFilterEndDatePicker(!showFilterEndDatePicker);
                        setShowFilterDatePicker(false);
                        setShowFilterStartDatePicker(false);
                        setShowAttendanceDatePicker(false);
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors cursor-pointer"
                      placeholder=""
                    />
                    {!attendanceFilters.endDate && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                        <span className="text-xs text-gray-400">Select date</span>
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {showFilterEndDatePicker && (
                      <div className="absolute top-full right-0 z-50 mt-2 p-2.5">
                        <Calendar
                          onChange={(date) => {
                            const formattedDate = date ? formatDateToISO(date) : '';
                            setAttendanceFilters((prev) => ({ ...prev, endDate: formattedDate }));
                            setShowFilterEndDatePicker(false);
                          }}
                          value={parseDateString(attendanceFilters.endDate)}
                          formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                          prevLabel={<span className="text-purple-600">‹</span>}
                          nextLabel={<span className="text-purple-600">›</span>}
                          prev2Label={null}
                          next2Label={null}
                          tileClassName={({ date, view }) => {
                            if (view === 'month') {
                              const day = date.getDay();
                              if (day === 0 || day === 6) {
                                return 'weekend-day';
                              }
                            }
                            return null;
                          }}
                />
              </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-[#444] uppercase tracking-wider opacity-0">
                    Actions
                  </label>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchAttendance()}
                      className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 hover:shadow-md transition-all duration-200"
                > 
                  Apply
                </button>
                <button
                  onClick={() => {
                    const cleared = {
                      employeeId: "",
                      status: "",
                      startDate: "",
                      endDate: "",
                      date: "",
                    };
                    setAttendanceFilters(cleared);
                    fetchAttendance(cleared);
                  }}
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Reset
                </button>
                  </div>
                </div>
              </div>
            </div>

            <AttendanceTable />
          </div>
        )}

        {activeTab === "timesheet" && (
          <div className="space-y-6">
            {/* Header with Tabs and Date Picker */}
            <div className="rounded-xl bg-white p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between gap-4">
                {/* Tabs */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setTimesheetView("day")}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${
                      timesheetView === "day"
                        ? "bg-indigo-600 text-white shadow-md"
                        : "bg-gray-100 text-[#444] hover:bg-gray-200"
                    }`}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    By Day
                  </button>
                  <button
                    onClick={() => setTimesheetView("week")}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${
                      timesheetView === "week"
                        ? "bg-purple-600 text-white shadow-md"
                        : "bg-gray-100 text-[#444] hover:bg-gray-200"
                    }`}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    By Week
                  </button>
                </div>

                {/* Date Picker - Right Aligned */}
                <div className="flex items-end gap-3">
                  {timesheetView === "day" ? (
                    <>
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 mb-0.5">
                        <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#444] uppercase tracking-wide mb-1.5">Select Date</label>
                        <div className="relative">
                <input
                            type="text"
                            readOnly
                            value={formatDateForDisplay(timesheetDate)}
                            onClick={() => {
                              setShowTimesheetDatePicker(!showTimesheetDatePicker);
                              setShowTimesheetWeekStartPicker(false);
                              setShowAllFilterWeekStartPicker(false);
                            }}
                            className="w-40 rounded-lg border border-gray-200 bg-white pl-3 pr-10 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors cursor-pointer"
                            placeholder=""
                          />
                          {!timesheetDate && (
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-10">
                              <span className="text-sm text-gray-400">Select date</span>
              </div>
                          )}
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          {showTimesheetDatePicker && (
                            <div className="absolute top-full right-0 z-50 mt-2 p-2.5">
                              <Calendar
                                onChange={(date) => {
                                  const formattedDate = date ? formatDateToISO(date) : '';
                                  setTimesheetDate(formattedDate);
                                  setShowTimesheetDatePicker(false);
                                }}
                                value={parseDateString(timesheetDate)}
                                formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                                prevLabel={<span className="text-purple-600">‹</span>}
                                nextLabel={<span className="text-purple-600">›</span>}
                                prev2Label={null}
                                next2Label={null}
                                tileClassName={({ date, view }) => {
                                  if (view === 'month') {
                                    const day = date.getDay();
                                    if (day === 0 || day === 6) {
                                      return 'weekend-day';
                                    }
                                  }
                                  return null;
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50 mb-0.5">
                        <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#444] uppercase tracking-wide mb-1.5">Select Week Start</label>
                        <div className="relative">
                <input
                            type="text"
                            readOnly
                            value={formatDateForDisplay(timesheetWeekStart)}
                            onClick={() => {
                              setShowTimesheetWeekStartPicker(!showTimesheetWeekStartPicker);
                              setShowTimesheetDatePicker(false);
                              setShowAllFilterWeekStartPicker(false);
                            }}
                            className="w-40 rounded-lg border border-gray-200 bg-white pl-3 pr-10 py-2.5 text-sm text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-colors cursor-pointer"
                            placeholder=""
                          />
                          {!timesheetWeekStart && (
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-10">
                              <span className="text-sm text-gray-400">Select date</span>
              </div>
                          )}
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
            </div>
                          {showTimesheetWeekStartPicker && (
                            <div className="absolute top-full right-0 z-50 mt-2 p-2.5">
                              <Calendar
                                onChange={(date) => {
                                  const formattedDate = date ? formatDateToISO(date) : '';
                                  setTimesheetWeekStart(formattedDate);
                                  setShowTimesheetWeekStartPicker(false);
                                }}
                                value={parseDateString(timesheetWeekStart)}
                                formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                                prevLabel={<span className="text-purple-600">‹</span>}
                                nextLabel={<span className="text-purple-600">›</span>}
                                prev2Label={null}
                                next2Label={null}
                                tileClassName={({ date, view }) => {
                                  if (view === 'month') {
                                    const day = date.getDay();
                                    if (day === 0 || day === 6) {
                                      return 'weekend-day';
                                    }
                                  }
                                  return null;
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Content based on selected tab */}
            {timesheetView === "day" && (
              <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-100">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600">
                      <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">By Day</h3>
                      <p className="text-xs text-gray-500">View daily attendance entries</p>
                    </div>
                  </div>
                </div>
              <DayTimesheet />
              </div>
            )}

            {timesheetView === "week" && (
              <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-100">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-purple-600">
                      <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">By Week</h3>
                      <p className="text-xs text-gray-500">View weekly attendance summary</p>
                    </div>
                  </div>
                </div>
              <WeekTimesheet />
              </div>
            )}
          </div>
        )}

        {activeTab === "all" && (
          <div className="space-y-4">
            <div className="rounded-xl bg-white px-6 py-4 shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                <div className="flex flex-col gap-2">
                <label
                  htmlFor="allFilterEmployee"
                    className="text-xs font-semibold text-[#444] uppercase tracking-wider"
                >
                  Employee
                </label>
                  <div className="relative">
                <select
                  id="allFilterEmployee"
                  value={attendanceFilters.employeeId}
                  onChange={(e) =>
                    setAttendanceFilters((prev) => ({ ...prev, employeeId: e.target.value }))
                  }
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors appearance-none"
                      style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                >
                  <option value="">All employees</option>
                  {employees.map((emp) => {
                    const id = emp.id || emp._id || emp.userId || emp.user_id;
                    return (
                      <option key={id} value={id}>
                        {getEmployeeName(id)}
                      </option>
                    );
                  })}
                </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
              </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                <label
                  htmlFor="allFilterStatus"
                    className="text-xs font-semibold text-[#444] uppercase tracking-wider"
                >
                    Employee Status
                </label>
                  <div className="relative">
                <select
                  id="allFilterStatus"
                  value={attendanceFilters.status}
                  onChange={(e) =>
                    setAttendanceFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors appearance-none"
                      style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
              </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                <label
                  htmlFor="allFilterWeekStart"
                    className="text-xs font-semibold text-[#444] uppercase tracking-wider"
                >
                    Week Starting
                </label>
                  <div className="relative">
                <input
                  id="allFilterWeekStart"
                      type="text"
                      readOnly
                      value={timesheetWeekStart ? new Date(timesheetWeekStart).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-') : ''}
                      onClick={() => {
                        setShowAllFilterWeekStartPicker(!showAllFilterWeekStartPicker);
                        setShowTimesheetDatePicker(false);
                        setShowTimesheetWeekStartPicker(false);
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-3 pr-11 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors cursor-pointer"
                      placeholder=""
                    />
                    {!timesheetWeekStart && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                        <span className="text-sm text-gray-400">Select date</span>
              </div>
                    )}
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {showAllFilterWeekStartPicker && (
                      <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                        <Calendar
                          onChange={(date) => {
                            const formattedDate = date ? formatDateToISO(date) : '';
                            setTimesheetWeekStart(formattedDate);
                            setShowAllFilterWeekStartPicker(false);
                          }}
                          value={parseDateString(timesheetWeekStart)}
                          formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                          prevLabel={<span className="text-purple-600">‹</span>}
                          nextLabel={<span className="text-purple-600">›</span>}
                          prev2Label={null}
                          next2Label={null}
                          tileClassName={({ date, view }) => {
                            if (view === 'month') {
                              const day = date.getDay();
                              if (day === 0 || day === 6) {
                                return 'weekend-day';
                              }
                            }
                            return null;
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-end gap-2">
                <button
                  onClick={fetchWeeklyTimesheet}
                    className="flex-1 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-[1px]"
                >
                  Apply
                </button>
                <button
                  onClick={() => {
                    const start = getLast7DaysStart();
                    setAttendanceFilters((prev) => ({
                      ...prev,
                      employeeId: "",
                      status: "",
                    }));
                    setTimesheetWeekStart(start);
                    fetchWeeklyTimesheet();
                  }}
                    className="flex-1 rounded-lg border-2 border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Reset
                </button>
                </div>
              </div>
            </div>

            <AllWeeklyTimeline />
          </div>
        )}

        {activeTab === "leave" && (
          <div className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Days
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {leaveList.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-sm font-medium text-gray-500">No leave requests</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {leaveList.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-purple-600 text-sm font-semibold text-white">
                              {getEmployeeName(item.employeeId).charAt(0).toUpperCase()}
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                          {getEmployeeName(item.employeeId)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                          {item.startDate} - {item.endDate}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{item.leaveType}</td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">{item.totalDays}</td>
                        <td className="px-6 py-3 whitespace-nowrap">{renderStatusBadge(item.status)}</td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => updateLeaveStatus(item, "approved")}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 transition-colors hover:bg-green-100"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Approve
                            </button>
                            <button
                              onClick={() => updateLeaveStatus(item, "disapproved")}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "wfh" && (
          <div className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Employee
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Duration
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Reason
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Status
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {wfhList.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            <p className="text-sm font-medium text-gray-500">No WFH requests</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {wfhList.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-sm font-semibold text-white">
                              {getEmployeeName(item.employeeId).charAt(0).toUpperCase()}
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                          {getEmployeeName(item.employeeId)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {item.startDate} - {item.endDate}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {item.reason || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{renderStatusBadge(item.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => updateWfhStatus(item, "approved")}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 transition-colors hover:bg-green-100"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Approve
                            </button>
                            <button
                              onClick={() => updateWfhStatus(item, "disapproved")}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "regularization" && (
          <div className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Employee
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Attendance Id
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Requested In/Out
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Reason
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Status
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {regularizationList.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm font-medium text-gray-500">No regularization requests</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {regularizationList.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-sm font-semibold text-white">
                              {getEmployeeName(item.employeeId).charAt(0).toUpperCase()}
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                          {getEmployeeName(item.employeeId)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.attendanceId}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {item.requestedClockIn
                            ? new Date(item.requestedClockIn).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "-"}
                          {" / "}
                          {item.requestedClockOut
                            ? new Date(item.requestedClockOut).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {item.reason || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{renderStatusBadge(item.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => updateRegularizationStatus(item, "approved")}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 transition-colors hover:bg-green-100"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Approve
                            </button>
                            <button
                              onClick={() => updateRegularizationStatus(item, "rejected")}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "penalty" && (
          <div className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Employee
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Reason
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                        Reference
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {penaltyList.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <p className="text-sm font-medium text-gray-500">No penalties</p>
                          </div>
                        </td>
                      </tr>
                    )}
                    {penaltyList.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-red-600 text-sm font-semibold text-white">
                              {getEmployeeName(item.employeeId).charAt(0).toUpperCase()}
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                          {getEmployeeName(item.employeeId)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.date}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {item.reason || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {item.amount || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {item.referenceType || <span className="text-gray-400">-</span>} {item.referenceId || ""}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={() => handleDeletePenalty(item.id)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Attendance modal */}
      <SimpleModal
        open={attendanceModalOpen}
        title={getAttendanceValues().id ? "Edit Attendance" : "Add Attendance"}
        onClose={() => setAttendanceModalOpen(false)}
        slideFromRight={true}
        footer={
          <>
            <button
              onClick={() => setAttendanceModalOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAttendanceSubmit(onSubmitAttendance)}
              className="rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 hover:shadow-md transition-all"
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid gap-5 md:grid-cols-2">
          {/* Employee */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="attendanceEmployee"
              className="text-xs font-normal text-[#444] uppercase tracking-wide"
            >
              Employee *
            </label>
            <div className="relative">
            <select
              id="attendanceEmployee"
              {...attendanceRegister("employeeId")}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors appearance-none"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
            >
              <option value="">Select</option>
              {employees.map((emp) => {
                const id = emp.id || emp._id || emp.userId || emp.user_id;
                return (
                  <option key={id} value={id}>
                    {getEmployeeName(id)}
                  </option>
                );
              })}
            </select>
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
          </div>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Date */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="attendanceDate"
              className="text-xs font-normal text-[#444] uppercase tracking-wide"
            >
              Date *
            </label>
            <div className="relative">
            <input
              id="attendanceDate"
                type="text"
                readOnly
                value={formatDateForDisplay(watchAttendance("date"))}
                onClick={() => {
                  setShowAttendanceDatePicker(!showAttendanceDatePicker);
                  setShowFilterDatePicker(false);
                  setShowFilterStartDatePicker(false);
                  setShowFilterEndDatePicker(false);
                }}
                className="w-full rounded-lg border border-gray-300 bg-white pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors cursor-pointer"
                placeholder=""
              />
              {!watchAttendance("date") && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
          </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showAttendanceDatePicker && (
                <div className="absolute top-full left-0 z-50 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setAttendanceValue("date", formattedDate);
                      setShowAttendanceDatePicker(false);
                    }}
                    value={parseDateString(watchAttendance("date"))}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Clock In */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="attendanceClockIn"
              className="text-xs font-normal text-[#444] uppercase tracking-wide"
            >
              Clock In
            </label>
            <div className="relative">
            <input
              id="attendanceClockIn"
              type="time"
              {...attendanceRegister("clockInTime")}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
          </div>
            </div>
          </div>

          {/* Clock Out */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="attendanceClockOut"
              className="text-xs font-normal text-[#444] uppercase tracking-wide"
            >
              Clock Out
            </label>
            <div className="relative">
            <input
              id="attendanceClockOut"
              type="time"
              {...attendanceRegister("clockOutTime")}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
          </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="attendanceStatus"
              className="text-xs font-normal text-[#444] uppercase tracking-wide"
            >
              Status
            </label>
            <div className="relative">
            <select
              id="attendanceStatus"
              {...attendanceRegister("status")}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 pr-10 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors appearance-none"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
            >
              <option value="present">Present</option>
              <option value="absent">Absent</option>
              <option value="late">Late</option>
            </select>
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
          </div>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="attendanceLocation"
              className="text-xs font-normal text-[#444] uppercase tracking-wide"
            >
              Location
            </label>
            <div className="relative">
            <input
              id="attendanceLocation"
              type="text"
              {...attendanceRegister("location")}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pl-10 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors"
              placeholder="Office / Remote"
            />
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
          </div>
            </div>
          </div>

          {/* Note */}
          <div className="md:col-span-2 flex flex-col gap-2">
            <label
              htmlFor="attendanceNote"
              className="text-xs font-normal text-[#444] uppercase tracking-wide"
            >
              Note
            </label>
            <textarea
              id="attendanceNote"
              rows={3}
              {...attendanceRegister("note")}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors resize-none"
              placeholder="Add any additional notes..."
            />
          </div>
        </div>
      </SimpleModal>

      {/* Leave modal */}
      <SimpleModal
        open={leaveModalOpen}
        title="Request Leave"
        onClose={() => setLeaveModalOpen(false)}
        slideFromRight={true}
        footer={
          <>
            <button
              onClick={() => setLeaveModalOpen(false)}
              className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLeaveSubmit(onSubmitLeave)}
              className="rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all hover:from-indigo-700 hover:to-indigo-600"
            >
              Request
            </button>
          </>
        }
      >
        <div className="space-y-6">
          {/* Employee Field - Required for functionality */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-[#444] uppercase tracking-wide">Employee *</label>
            <div className="relative">
            <select
              {...leaveRegister("employeeId")}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-xs text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-colors appearance-none"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
            >
              <option value="">Select</option>
              {employees.map((emp) => {
                const id = emp.id || emp._id || emp.userId || emp.user_id;
                return (
                  <option key={id} value={id}>
                    {getEmployeeName(id)}
                  </option>
                );
              })}
            </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
          </div>
          </div>
          </div>

          {/* Date Range with Days Counter */}
          <div>
            <div className="grid grid-cols-3 gap-4 items-end">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-normal text-[#444] uppercase tracking-wide">From</label>
                <div className="relative">
            <input
                    type="text"
                    readOnly
                    value={formatDateForDisplay(leaveStartDate)}
                    onClick={() => {
                      setShowStartDatePicker(!showStartDatePicker);
                      setShowEndDatePicker(false);
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-white pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-colors cursor-pointer"
                    placeholder=""
                  />
                  {!leaveStartDate && (
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                      <span className="text-xs text-gray-400">Select date</span>
          </div>
                  )}
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  {showStartDatePicker && (
                    <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                      <Calendar
                        onChange={(date) => {
                          const formattedDate = date ? formatDateToISO(date) : '';
                          setLeaveValue("startDate", formattedDate, { shouldValidate: true, shouldDirty: true });
                          // If end date is before start date, clear it
                          const currentEndDate = watchLeave("endDate");
                          if (formattedDate && currentEndDate) {
                            const start = parseDateString(formattedDate);
                            const end = parseDateString(currentEndDate);
                            if (start && end) {
                              if (end < start) {
                                // Clear TO date if it's before FROM date
                                setLeaveValue("endDate", "", { shouldValidate: true, shouldDirty: true });
                                setLeaveValue("totalDays", "", { shouldValidate: true, shouldDirty: true });
                              } else {
                                const diffTime = Math.abs(end - start);
                                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
                                setLeaveValue("totalDays", diffDays.toString(), { shouldValidate: true, shouldDirty: true });
                              }
                            }
                          } else if (!formattedDate || !currentEndDate) {
                            setLeaveValue("totalDays", "", { shouldValidate: true, shouldDirty: true });
                          }
                          setShowStartDatePicker(false);
                        }}
                        value={parseDateString(leaveStartDate)}
                        formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                        prevLabel={<span className="text-purple-600">‹</span>}
                        nextLabel={<span className="text-purple-600">›</span>}
                        prev2Label={null}
                        next2Label={null}
                        tileClassName={({ date, view }) => {
                          if (view === 'month') {
                            const day = date.getDay();
                            if (day === 0 || day === 6) {
                              return 'weekend-day';
                            }
                          }
                          return null;
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-normal text-[#444] uppercase tracking-wide opacity-0">Days</label>
                <div className="relative">
            <input
                    type="number"
                    {...leaveRegister("totalDays")}
                    value={leaveTotalDays || ""}
                    min="0"
                    disabled
                    readOnly
                    className="w-full rounded-full bg-gray-100 border border-gray-200 px-4 py-2.5 pr-16 text-center text-xs font-semibold text-gray-700 cursor-not-allowed opacity-100"
                    placeholder="0"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-xs font-semibold text-gray-700">
                    days
                  </span>
          </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-normal text-[#444] uppercase tracking-wide">To</label>
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value={formatDateForDisplay(leaveEndDate)}
                    onClick={() => {
                      setShowEndDatePicker(!showEndDatePicker);
                      setShowStartDatePicker(false);
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-white pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-colors cursor-pointer"
                    placeholder=""
                  />
                  {!leaveEndDate && (
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                      <span className="text-xs text-gray-400">Select date</span>
                    </div>
                  )}
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  {showEndDatePicker && (
                    <div className="absolute top-full right-0 z-50 mt-2 p-2.5">
                      <Calendar
                        onChange={(date) => {
                          const formattedDate = date ? formatDateToISO(date) : '';
                          setLeaveValue("endDate", formattedDate, { shouldValidate: true, shouldDirty: true });
                          // Calculate days immediately if start date exists
                          const currentStartDate = watchLeave("startDate");
                          if (formattedDate && currentStartDate) {
                            const start = parseDateString(currentStartDate);
                            const end = parseDateString(formattedDate);
                            if (start && end) {
                              const diffTime = Math.abs(end - start);
                              const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
                              setLeaveValue("totalDays", diffDays.toString(), { shouldValidate: true, shouldDirty: true });
                            }
                          } else if (!formattedDate || !currentStartDate) {
                            setLeaveValue("totalDays", "", { shouldValidate: true, shouldDirty: true });
                          }
                          setShowEndDatePicker(false);
                        }}
                        value={parseDateString(leaveEndDate)}
                        minDate={leaveStartDate ? parseDateString(leaveStartDate) : null}
                        tileDisabled={({ date, view }) => {
                          if (view === 'month' && leaveStartDate) {
                            const checkDate = new Date(date);
                            checkDate.setHours(0, 0, 0, 0);
                            const startDate = parseDateString(leaveStartDate);
                            if (startDate) {
                              startDate.setHours(0, 0, 0, 0);
                              // Disable dates before FROM date
                              return checkDate < startDate;
                            }
                          }
                          return false;
                        }}
                        formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                        prevLabel={<span className="text-purple-600">‹</span>}
                        nextLabel={<span className="text-purple-600">›</span>}
                        prev2Label={null}
                        next2Label={null}
                        tileClassName={({ date, view }) => {
                          if (view === 'month') {
                            const day = date.getDay();
                            if (day === 0 || day === 6) {
                              return 'weekend-day';
                            }
                          }
                          return null;
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Leave Type */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Select type of leave you want to apply</label>
            <div className="relative">
              <select
                {...leaveRegister("leaveType")}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-xs text-gray-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-colors appearance-none"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
              >
                <option value="">Select</option>
                <option value="Sick Leave">Sick Leave</option>
                <option value="Casual Leave">Casual Leave</option>
                <option value="Earned Leave">Earned Leave</option>
                <option value="Unpaid Leave">Unpaid Leave</option>
                <option value="Medical Leave">Medical Leave</option>
                <option value="Bereavement Leave">Bereavement Leave</option>
                <option value="Parental Leave">Parental Leave</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Number of Days - Auto-calculated but kept for functionality */}
          <div className="hidden">
            <input
              type="number"
              {...leaveRegister("totalDays")}
            />
          </div>

          {/* Note */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Note</label>
            <textarea
              {...leaveRegister("reason")}
              rows={4}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-xs text-gray-900 placeholder:text-gray-400 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-colors resize-none"
              placeholder="Type here"
            />
          </div>
        </div>
      </SimpleModal>

      {/* WFH modal */}
      <SimpleModal
        open={wfhModalOpen}
        title="WFH Request"
        onClose={() => setWfhModalOpen(false)}
        slideFromRight={true}
        footer={
          <>
            <button
              onClick={() => setWfhModalOpen(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleWfhSubmit(onSubmitWfh)}
              className="rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all hover:from-indigo-700 hover:to-indigo-600"
            >
              Request
            </button>
          </>
        }
      >
        <div className="grid gap-4 grid-cols-1">
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Employee *</label>
            <select
              {...wfhRegister("employeeId")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 appearance-none"
              style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
            >
              <option value="">Select</option>
              {employees.map((emp) => {
                const id = emp.id || emp._id || emp.userId || emp.user_id;
                return (
                  <option key={id} value={id}>
                    {getEmployeeName(id)}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Start Date *</label>
            <div className="relative mt-1">
            <input
                type="text"
                readOnly
                value={formatDateForDisplay(watchWfh("startDate"))}
                onClick={() => {
                  setShowWfhStartDatePicker(!showWfhStartDatePicker);
                  setShowWfhEndDatePicker(false);
                }}
                className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-colors cursor-pointer"
                placeholder=""
              />
              {!watchWfh("startDate") && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showWfhStartDatePicker && (
                <div className="absolute top-full left-0 z-50 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setWfhValue("startDate", formattedDate);
                      setShowWfhStartDatePicker(false);
                    }}
                    value={parseDateString(watchWfh("startDate"))}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">End Date *</label>
            <div className="relative mt-1">
            <input
                type="text"
                readOnly
                value={formatDateForDisplay(watchWfh("endDate"))}
                onClick={() => {
                  setShowWfhEndDatePicker(!showWfhEndDatePicker);
                  setShowWfhStartDatePicker(false);
                }}
                className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-colors cursor-pointer"
                placeholder=""
              />
              {!watchWfh("endDate") && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showWfhEndDatePicker && (
                <div className="absolute top-full right-0 z-50 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setWfhValue("endDate", formattedDate);
                      setShowWfhEndDatePicker(false);
                    }}
                    value={parseDateString(watchWfh("endDate"))}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Reason</label>
            <textarea
              rows={3}
              {...wfhRegister("reason")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
        </div>
      </SimpleModal>

      {/* Regularization modal */}
      <SimpleModal
        open={regularizationModalOpen}
        title="Regularization Request"
        onClose={() => setRegularizationModalOpen(false)}
        slideFromRight={true}
        footer={
          <>
            <button
              onClick={() => setRegularizationModalOpen(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleRegularizationSubmit(onSubmitRegularization)}
              className="rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all hover:from-indigo-700 hover:to-indigo-600"
            >
              Request
            </button>
          </>
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">
              Attendance Record *
            </label>
            <select
              {...regularizationRegister("attendanceId")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue appearance-none"
              style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
            >
              <option value="">Select attendance</option>
              {attendance.map((rec) => (
                <option key={rec.id} value={rec.id}>
                  {rec.date} • {getEmployeeName(rec.employeeId)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Employee *</label>
            <select
              {...regularizationRegister("employeeId")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue appearance-none"
              style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
            >
              <option value="">Select</option>
              {employees.map((emp) => {
                const id = emp.id || emp._id || emp.userId || emp.user_id;
                return (
                  <option key={id} value={id}>
                    {getEmployeeName(id)}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Requested Clock In</label>
            <input
              type="time"
              {...regularizationRegister("requestedClockIn")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Requested Clock Out</label>
            <input
              type="time"
              {...regularizationRegister("requestedClockOut")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Reason</label>
            <textarea
              rows={3}
              {...regularizationRegister("reason")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            />
          </div>
        </div>
      </SimpleModal>

      {/* Penalty modal */}
      <SimpleModal
        open={penaltyModalOpen}
        title="Create Penalty"
        onClose={() => setPenaltyModalOpen(false)}
        slideFromRight={true}
        footer={
          <>
            <button
              onClick={() => setPenaltyModalOpen(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handlePenaltySubmit(onSubmitPenalty)}
              className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md"
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Employee *</label>
            <select
              {...penaltyRegister("employeeId")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue appearance-none"
              style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
            >
              <option value="">Select</option>
              {employees.map((emp) => {
                const id = emp.id || emp._id || emp.userId || emp.user_id;
                return (
                  <option key={id} value={id}>
                    {getEmployeeName(id)}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Date *</label>
            <div className="relative mt-1">
            <input
                type="text"
                readOnly
                value={formatDateForDisplay(watchPenalty("date"))}
                onClick={() => {
                  setShowPenaltyDatePicker(!showPenaltyDatePicker);
                }}
                className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-colors cursor-pointer"
                placeholder=""
              />
              {!watchPenalty("date") && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                  <span className="text-xs text-gray-400">Select date</span>
                </div>
              )}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              {showPenaltyDatePicker && (
                <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                  <Calendar
                    onChange={(date) => {
                      const formattedDate = date ? formatDateToISO(date) : '';
                      setPenaltyValue("date", formattedDate);
                      setShowPenaltyDatePicker(false);
                    }}
                    value={parseDateString(watchPenalty("date"))}
                    formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    prevLabel={<span className="text-purple-600">‹</span>}
                    nextLabel={<span className="text-purple-600">›</span>}
                    prev2Label={null}
                    next2Label={null}
                    tileClassName={({ date, view }) => {
                      if (view === 'month') {
                        const day = date.getDay();
                        if (day === 0 || day === 6) {
                          return 'weekend-day';
                        }
                      }
                      return null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Amount</label>
            <input
              type="number"
              {...penaltyRegister("amount")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
              placeholder="e.g. 500"
            />
          </div>
          <div>
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Reference Type</label>
            <input
              type="text"
              {...penaltyRegister("referenceType")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
              placeholder="attendance/leave"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Reference Id</label>
            <input
              type="text"
              {...penaltyRegister("referenceId")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
              placeholder="Optional"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-normal text-[#444] uppercase tracking-wide">Reason</label>
            <textarea
              rows={3}
              {...penaltyRegister("reason")}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
            />
          </div>
        </div>
      </SimpleModal>

      <ToastContainer />
    </div>
  );
};

export default Attendance;

